import { useEffect } from 'react'

type WakeLockSentinel = {
  release: () => Promise<void>
  addEventListener?: (type: 'release', listener: () => void) => void
}
type NavigatorWithWakeLock = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> }
}

/**
 * Hold a screen wake-lock while `active` is true. Extracted from
 * useAudioPlayer's original "keep the screen on while audio plays" effect
 * so the same mechanism can also cover silent READING surfaces (the Quran
 * reader page, the public SEO surah page, and the playlist's pinned
 * full-lyrics / immersive view) — not just active playback.
 *
 * On mobile a long surah (or a long silent read) otherwise gets cut short
 * the moment the screen auto-locks (iOS backgrounds the page, Android
 * suspends work). The API is gated behind a try/catch because (a) it
 * requires a secure context, (b) older browsers don't expose it, and (c)
 * the lock can be revoked by the OS at any moment. Revocation is why we
 * listen to the sentinel's own `release` event: the OS drops the lock every
 * time the page is backgrounded (phone locked, app switched), and a hook
 * that kept its dead sentinel would believe it still held one and never
 * re-request — so all playback after the first background cycle ran with no
 * lock and the screen slept. Clearing the reference on release lets the
 * `visibilitychange` handler re-acquire when the user comes back.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    const nav = navigator as NavigatorWithWakeLock
    if (!nav.wakeLock) return
    let sentinel: WakeLockSentinel | null = null
    let cancelled = false
    const acquire = async () => {
      try {
        const s = await nav.wakeLock!.request('screen')
        if (cancelled) {
          s.release().catch(() => {})
          return
        }
        sentinel = s
        // Drop our reference the moment the OS revokes the lock, so the
        // visibilitychange handler below can request a fresh one.
        s.addEventListener?.('release', () => {
          if (sentinel === s) sentinel = null
        })
      } catch {
        // permission denied / not allowed / OS refusal — nothing we can do
      }
    }
    acquire()
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && active && !sentinel) {
        acquire()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      sentinel?.release().catch(() => {})
      sentinel = null
    }
  }, [active])
}
